import { ITranslationManager } from "@fenetech/translations";
import { QuoteRequiredFieldEnum } from "helpers/enums"
import { isNullOrWhiteSpace, ResolvePath, ResolvePathNullable } from "helpers/objects";
import { IQuote } from "models/IQuote";

interface IExpanderState {
    [x: string]: boolean;
    info: boolean;
    sales: boolean;
    billing: boolean;
    shipping: boolean;
}

export default class QuoteRequiredFieldHelpers {
    private static FormFieldsByGroup = {
        "info": ["poNumber", "customerRef", "orderContact"],
        "sales": ["salespersonID", "billing.terms"],
        "shipping": ["shipping.address.address1", "shipping.address.city", "shipping.address.state", "shipping.address.zipCode", "shipping.phone", "shipping.fax"],
        "billing": ["billing.address.address1", "billing.address.city", "billing.address.state", "billing.address.zipCode", "billing.phone", "billing.fax"]
    }

    private static EnumValuesByGroup = {
        "info": [QuoteRequiredFieldEnum.PONumber, QuoteRequiredFieldEnum.CustomerRef, QuoteRequiredFieldEnum.QuoteContact],
        "sales": [QuoteRequiredFieldEnum.Salesperson, QuoteRequiredFieldEnum.Terms],
        "shipping": [QuoteRequiredFieldEnum.ShippingAddress, QuoteRequiredFieldEnum.ShippingPhone, QuoteRequiredFieldEnum.ShippingFax],
        "billing": [QuoteRequiredFieldEnum.BillingAddress, QuoteRequiredFieldEnum.BillingPhone, QuoteRequiredFieldEnum.BillingFax]
    }

    private static FormFieldsByEnum = {
        [QuoteRequiredFieldEnum.PONumber]: ["poNumber"],
        [QuoteRequiredFieldEnum.CustomerRef]: ["customerRef"],
        [QuoteRequiredFieldEnum.QuoteContact]: ["quoteContact"],
        [QuoteRequiredFieldEnum.Salesperson]: ["salespersonID"],
        [QuoteRequiredFieldEnum.Terms]: ["billing.terms"],
        [QuoteRequiredFieldEnum.ShippingAddress]: ["shipping.address.address1", "shipping.address.city", "shipping.address.state", "shipping.address.zipCode"],
        [QuoteRequiredFieldEnum.ShippingPhone]: ["shipping.phone"],
        [QuoteRequiredFieldEnum.ShippingFax]: ["shipping.fax"],
        [QuoteRequiredFieldEnum.BillingAddress]: ["billing.address.address1", "billing.address.city", "billing.address.state", "billing.address.zipCode"],
        [QuoteRequiredFieldEnum.BillingPhone]: ["billing.phone"],
        [QuoteRequiredFieldEnum.BillingFax]: ["billing.fax"],
    };

    public static GetExpandedStateForRequiredFields = (requiredFields: QuoteRequiredFieldEnum[] | undefined, quote: IQuote) => {
        let newExpandedState = {
            info: false,
            sales: false,
            billing: false,
            shipping: false,
        }
        
        const quoteGroupingAndEnums = this.EnumValuesByGroup;
        const enumValuesAndFields = this.FormFieldsByEnum;

        //Auto open any sections that have required fields that aren't filled in
        if (requiredFields && requiredFields.length > 0) {
            for (const group of Object.keys(quoteGroupingAndEnums)) {
                enumValueLoop: for (const enumValue of quoteGroupingAndEnums[group]) {
                    if (requiredFields.includes(enumValue)) {
                        for (const fieldName of enumValuesAndFields[enumValue]) {
                            const fieldValue = ResolvePath(fieldName, quote);
                            if (enumValue === QuoteRequiredFieldEnum.Salesperson) {
                                //Salesperson blank is treated as 0
                                if (fieldValue === 0) {
                                    newExpandedState[group] = true;
                                    break enumValueLoop;
                                }
                            } else if (isNullOrWhiteSpace(fieldValue)) {
                                newExpandedState[group] = true;
                                break enumValueLoop;
                            } 
                        }
                    }
                }
            }
        }

        return newExpandedState;
    }

    public static ReconcileQuoteValidations = (expandedState: IExpanderState, errors: { [x: string]: any }, tm: ITranslationManager) => {
        const groupValidations : string[] = [];
        const newExpandedState = expandedState;
    
        const quoteGroupingAndFields = this.FormFieldsByGroup;
    
        for (const key of Object.keys(quoteGroupingAndFields)) {
            for (const fieldName of quoteGroupingAndFields[key]) {
                const fieldErrors = ResolvePathNullable(fieldName, errors);
                if (fieldErrors && fieldErrors.hasOwnProperty("message")) {
                    switch(key) {
                        case "info": {
                            groupValidations.push(tm.Get("Quote Information Incomplete"));
                            newExpandedState[key] = true;
                            break;
                        }
                        case "sales": {
                            groupValidations.push(tm.Get("Sales Information Incomplete"));
                            newExpandedState[key] = true;
                            break;
                        }
                        case "shipping": {
                            groupValidations.push(tm.Get("Shipping Information Incomplete"));
                            newExpandedState[key] = true;
                            break;
                        }
                        case "billing": {
                            groupValidations.push(tm.Get("Billing Information Incomplete"));
                            newExpandedState[key] = true;
                            break;
                        }
                    }
                    break;
                }
            }
        }

        return { groupValidations, newExpandedState };
    }
}